import { THEMES, DEFAULT_THEME_KEY, THEME_STORAGE_KEY } from './themes';

// Resolve the active theme key from persisted storage (falls back to default).
export const getActiveThemeKey = () => {
  try {
    // Auth screens (login / signup / forgot password) are only shown when
    // logged out and are intentionally un-themed — they always use the default
    // dark theme. Custom themes apply only once a user is signed in.
    if (!sessionStorage.getItem('token')) return DEFAULT_THEME_KEY;
    const key = localStorage.getItem(THEME_STORAGE_KEY);
    if (key && THEMES[key]) return key;
  } catch (e) {
    // storage may be unavailable (private mode / SSR) — ignore.
  }
  return DEFAULT_THEME_KEY;
};

// Persist the chosen theme key (per-device boot cache).
export const setActiveThemeKey = (key) => {
  try {
    if (THEMES[key]) localStorage.setItem(THEME_STORAGE_KEY, key);
  } catch (e) {
    // ignore
  }
};

// Raw per-device theme choice straight from storage, ignoring auth state.
// Returns the key only if it maps to a known theme, else null. Used by the
// login-time reconciliation to know whether THIS device has a saved choice — a
// device's own choice must win over a stale backend value on the post-switch
// reload so a deliberate switch is never reverted.
export const getStoredThemeKey = () => {
  try {
    const key = localStorage.getItem(THEME_STORAGE_KEY);
    return key && THEMES[key] ? key : null;
  } catch (e) {
    return null;
  }
};

// The theme key the app BOOTED with, captured once at module load. `Colors`
// (and any module-level styles / static CSS that read it at import time) is
// built from this, so it's the source of truth for "what palette is actually
// live right now". Stays fixed for the page's lifetime — unlike
// `getActiveThemeKey()`, which flips from forced-dark to the stored value the
// instant a token lands at login. Compare against THIS to decide whether a
// reload is needed to converge on a newly-known theme.
const BOOT_THEME_KEY = getActiveThemeKey();
export const getBootThemeKey = () => BOOT_THEME_KEY;

// The live color object consumed across the app. Initialised from the booted
// theme. Its reference is stable — `applyThemeColors` mutates it in place so
// every `Colors.X` reader picks up new values on the next render.
export const Colors = { ...THEMES[BOOT_THEME_KEY].colors };

// Swap the active palette in place (keeps the same `Colors` reference).
export const applyThemeColors = (key) => {
  const theme = THEMES[key] || THEMES[DEFAULT_THEME_KEY];
  Object.assign(Colors, theme.colors);
  return Colors;
};

// True when the active theme renders on light surfaces. Read at call time so it
// tracks the theme that's live after a reload.
export const isLightTheme = () => THEMES[getActiveThemeKey()]?.mode === 'light';
